import type { PostHog } from "posthog-js";

import { scheduleAfterLoadIdle } from "@/lib/after-load-idle";
import { envClient } from "@/env.client";

// posthog-js is ~67 KB gzipped — statically importing it anywhere on the
// critical path hoists it into the earliest client entry, where it competes
// with the LCP image for bandwidth on throttled mobile (measured ~+0.4s
// simulated LCP on the experience page). Everything goes through this one
// idempotent loader so init happens exactly once, off the critical path, and
// callers like identify never race an uninitialized client.
let loading: Promise<PostHog | null> | null = null;

export function loadPostHog(): Promise<PostHog | null> {
  const key = envClient.NEXT_PUBLIC_POSTHOG_KEY;
  loading ??= key
    ? import("posthog-js").then(({ default: posthog }) => {
        // Autocapture and session recording stay off — storefront perf bar.
        posthog.init(key, {
          api_host: envClient.NEXT_PUBLIC_POSTHOG_HOST,
          autocapture: false,
          disable_session_recording: true,
        });
        return posthog;
      })
    : Promise.resolve(null);
  return loading;
}

/** Load + init after the window `load` event, during idle time. The initial
 *  `$pageview` fires at init, so a visitor who bounces before idle is not
 *  captured, and one who soft-navigates before idle has the entry pageview
 *  attributed to the second URL — accepted trade-offs: analytics never
 *  outranks the LCP window.
 *
 *  Skipped entirely on `/claim`. That page's URL contains a bearer credential
 *  valid for 30 days, and PostHog reads `location.href` itself when it captures
 *  the initial `$pageview` — so there is no call site here to redact, the way
 *  there is for our own beacon (`lib/telemetry-url.ts`). Not initialising is the
 *  one mitigation that does not depend on a vendor config option behaving as
 *  documented.
 *
 *  The cost is product analytics on a post-purchase receipt page that is already
 *  `noindex` and outside the ISR tree.
 *
 *  The durable fix this comment called for — ADR-0010's deferred cookie
 *  exchange — has since landed: `/claim` is a route handler that moves the token
 *  into an httpOnly cookie and redirects, so no PostHog init happens on that URL
 *  and this guard no longer has anything to catch. Kept for the reason its twin
 *  in `telemetry-url.ts` is kept: it is one comparison, and it is the control
 *  that has to already exist if anything is ever rendered under `/claim`
 *  again. */
export function scheduleIdlePostHogLoad(): void {
  if (typeof window !== "undefined" && isCredentialBearingRoute()) return;
  scheduleAfterLoadIdle(() => void loadPostHog());
}

/** Kept in step with `REDACTED_PATH_PREFIXES` in `lib/telemetry-url.ts`. */
function isCredentialBearingRoute(): boolean {
  const path = window.location.pathname;
  return path === "/claim" || path.startsWith("/claim/");
}
